import React, { useRef, useState } from 'react';
import { Tooltip } from 'antd';
import PageHeader from 'components/pageHeader';
import UserAvatar from 'components/userAvatar';
import { CameraFilled } from '@ant-design/icons';
import { COLORS } from 'components/UserDashboardV2/utils/constants';
import { fluid, fluidVh } from 'components/UserDashboardV2/utils/responsive';
import { sanitizeName } from 'components/UserDashboardV2/utils/formatters';
import { PhoneIcon, MailIcon } from './ProfileCommonComponents';
import { resolveStaffImageUrl, ghostButtonStyle, formatPhoneNumber } from './utils';
import { TABS, TAB_DISPLAY_ORDER } from './constants';
import ImagePreviewOverlay from './ImagePreviewOverlay';

// Profile name cut off with "..." when it doesn't fit; hovering shows the full
// name in a tooltip (only when it's actually cut off).
const TruncatedName = ({ name, style }) => {
    const ref = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    return (
        <Tooltip title={isTruncated ? name : ""}>
            <span
                ref={ref}
                onMouseEnter={() => {
                    const el = ref.current;
                    setIsTruncated(Boolean(el && el.scrollWidth > el.clientWidth));
                }}
                style={style}
            >
                {name}
            </span>
        </Tooltip>
    );
};

import TeacherIcon from '../../../Assets/userManagementV3/teacherIcon.svg';
import OperationIcon from '../../../Assets/userManagementV3/operationIcon.svg';

const ProfileHeader = ({
    headerRef,
    isStaffRoute,
    history,
    location,
    basePath,
    specificUserProfile,
    userRowDetails,
    editingSection,
    setEditingSection,
    draftProfile,
    setDraftProfile,
    savingSection,
    handleSaveSection,
    handleCancelSection,
    handleToggleStatus,
    handleDelete,
    handleEditClick,
    handleAvatarClick,
    avatarHovered,
    setAvatarHovered,
    imgUrl,
    imageFile,
    pendingPhotoRemoval,
    isActive,
    status,
    roleStr,
    activeTab,
    handleTabClick,
    enrolledCoursesCount,
}) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const avatarSrc = resolveStaffImageUrl(imgUrl || (pendingPhotoRemoval ? null : specificUserProfile?.image));
    const isEditingBanner = editingSection === 'BANNER';
    // Outside edit mode a click previews the photo (only when there is one — not the initials fallback).
    const canPreview = !isEditingBanner && !!avatarSrc;

    return (
        <div ref={headerRef} style={{ flexShrink: 0 }}>
            {/* Page title row */}
            <div style={{ marginBottom: fluid(20), flexShrink: 0 }}>
                <PageHeader
                    title="User profile"
                    onBackPress={() => {
                        const returnTo = location?.state?.returnTo;
                        if (returnTo) {
                            history.push(returnTo, location?.state?.returnState);
                        } else if (isStaffRoute) {
                            history.push(basePath);
                        } else if (window.history.length > 2) {
                            history.goBack();
                        } else {
                            history.push(`${basePath}/students`);
                        }
                    }}
                />
            </div>

            {/* Header card */}
            <div style={{
                backgroundColor: "#FFFFFF",
                borderRadius: fluid(24),
                padding: `${fluid(24)} ${fluid(32)}`,
                marginTop: fluid(80),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: fluid(20),
                minHeight: fluidVh(168),
                boxSizing: "border-box",
                flexShrink: 0,
            }}>
                {/* flex-basis 0 + minWidth: a long name shrinks (ellipsis) instead
                    of pushing the action buttons onto the next line. */}
                <div style={{ display: "flex", alignItems: "center", gap: fluid(24), flex: "1 1 0", minWidth: fluid(320) }}>
                    <div
                        style={{
                            position: "relative",
                            display: "inline-flex",
                            borderRadius: "50%",
                            overflow: "hidden",
                            flexShrink: 0,
                            cursor: isEditingBanner || canPreview ? "pointer" : "default",
                        }}
                        onMouseEnter={() => setAvatarHovered(true)}
                        onMouseLeave={() => setAvatarHovered(false)}
                        onClick={isEditingBanner ? handleAvatarClick : () => canPreview && setPreviewOpen(true)}
                    >
                        <UserAvatar
                            size={120}
                            shape="circle"
                            name={specificUserProfile?.name}
                            img={avatarSrc}
                            fontSize={fluid(48)}
                        />
                        {editingSection === 'BANNER' && avatarHovered && (
                            <div style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: 0,
                                height: "36%",
                                backgroundColor: "rgba(0,0,0,0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxSizing: "border-box",
                            }}>
                                <CameraFilled style={{ color: "#FFFFFF", fontSize: fluid(24) }} />
                            </div>
                        )}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: fluid(18), flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: fluid(14), minWidth: 0 }}>
                            {editingSection === 'BANNER' ? (
                                // Input grows with the name: a hidden copy of the text
                                // in the same grid cell sets the width (min 250, up to
                                // the available space), so long names aren't cut off.
                                <div style={{ display: "inline-grid", minWidth: fluid(250), maxWidth: "100%", flex: "0 1 auto" }}>
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            gridArea: "1 / 1",
                                            visibility: "hidden",
                                            whiteSpace: "pre",
                                            overflow: "hidden",
                                            fontSize: fluid(32),
                                            fontWeight: 700,
                                            lineHeight: "normal",
                                            paddingRight: fluid(4),
                                        }}
                                    >
                                        {draftProfile.name || " "}
                                    </span>
                                    <input
                                        value={draftProfile.name || ""}
                                        onChange={(e) => setDraftProfile({ ...draftProfile, name: sanitizeName(e.target.value) })}
                                        style={{
                                            gridArea: "1 / 1",
                                            fontSize: fluid(32),
                                            fontWeight: 700,
                                            color: COLORS.textBlack,
                                            border: 'none',
                                            borderBottom: '1px solid #AEAEAE',
                                            backgroundColor: 'transparent',
                                            outline: 'none',
                                            width: "100%",
                                            minWidth: 0,
                                            padding: 0,
                                            margin: 0,
                                            lineHeight: "normal",
                                            boxSizing: "border-box",
                                            fontFamily: "inherit",
                                            // Full name, scrollable inside the field.
                                            overflow: "auto",
                                        }}
                                    />
                                </div>
                            ) : (
                                <TruncatedName
                                    name={specificUserProfile?.name || userRowDetails?.name || "NA"}
                                    style={{ fontSize: fluid(32), fontWeight: 700, color: COLORS.textBlack, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, maxWidth: fluid(420) }}
                                />
                            )}
                            {/* Status dot beside the name removed. */}
                            {/* <span
                                title={status}
                                style={{ width: fluid(10), height: fluid(10), borderRadius: "50%", backgroundColor: isActive ? "#00A566" : "#FF414D", flexShrink: 0 }}
                            /> */}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: fluid(24), flexWrap: "wrap" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: fluid(8), fontSize: fluid(16), color: COLORS.textLight }}>
                                <PhoneIcon />
                                {formatPhoneNumber(specificUserProfile?.phone || userRowDetails?.phone)}
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: fluid(8), fontSize: fluid(16), color: COLORS.textLight }}>
                                <MailIcon />
                                {specificUserProfile?.email || userRowDetails?.email || "NA"}
                            </span>
                        </div>
                        {isStaffRoute && editingSection === 'BANNER' ? (
                            <div style={{ display: "flex", gap: fluid(10) }}>
                                {[{ value: "Teacher", label: "Teacher" }, { value: "Operation", label: "Operations" }].map((opt) => {
                                    const isSelected = (draftProfile.roles || []).includes(opt.value) ||
                                        (opt.value === "Operation" && (draftProfile.roles || []).some(r => String(r || "").toLowerCase().includes("operation")));
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setDraftProfile({ ...draftProfile, roles: [opt.value] })}
                                            style={{
                                                height: fluid(40),
                                                padding: `0 ${fluid(22)}`,
                                                borderRadius: fluid(1000),
                                                border: isSelected ? "none" : `1px solid ${COLORS.borderLight}`,
                                                backgroundColor: isSelected ? COLORS.primaryLight : "#FFFFFF",
                                                color: isSelected ? COLORS.textBlack : COLORS.textLight,
                                                fontSize: fluid(16),
                                                fontFamily: "Roboto, sans-serif",
                                                cursor: "pointer",
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : roleStr && (
                            <span style={{ display: "flex", alignItems: "center", gap: fluid(8), fontSize: fluid(16), color: COLORS.textLight }}>
                                <img
                                    src={roleStr.toLowerCase().includes("operation") ? OperationIcon : TeacherIcon}
                                    alt={roleStr}
                                    style={{ width: fluid(24), height: fluid(24), flexShrink: 0 }}
                                />
                                {roleStr.toLowerCase().includes("operation") ? "Operations" : roleStr}
                            </span>
                        )}
                    </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: fluid(10), flexShrink: 0 }}>
                    {editingSection === 'BANNER' ? (() => {
                        const initialName = (specificUserProfile?.name || userRowDetails?.name || "").trim();
                        const draftName = (draftProfile.name || "").trim();
                        const isNameChanged = draftName !== initialName;

                        const initialRoles = (
                            Array.isArray(specificUserProfile?.role)
                                ? specificUserProfile.role
                                : (specificUserProfile?.role ? [specificUserProfile?.role] : [])
                        ).map(r => String(r).toLowerCase()).sort();
                        const currentRoles = (draftProfile.roles || []).map(r => String(r).toLowerCase()).sort();
                        const isRolesChanged = JSON.stringify(initialRoles) !== JSON.stringify(currentRoles);

                        const hasInitialPhoto = Boolean(
                            specificUserProfile?.image &&
                            specificUserProfile?.image !== "default.png" &&
                            !String(specificUserProfile?.image).includes("default.png")
                        );
                        const isPhotoChanged = Boolean(imageFile) || (hasInitialPhoto && Boolean(pendingPhotoRemoval));
                        const hasBannerChanged = isNameChanged || isRolesChanged || isPhotoChanged;
                        const isBannerValid = Boolean(draftName);
                        const isBannerSaveDisabled = !hasBannerChanged || !isBannerValid;

                        return (
                            <>
                                <button
                                    type="button"
                                    onClick={handleCancelSection || (() => {
                                        setEditingSection(null);
                                        setDraftProfile({});
                                    })}
                                    style={ghostButtonStyle("#AEAEAE")}
                                >
                                    CANCEL
                                </button>
                                <button
                                    type="button"
                                    disabled={isBannerSaveDisabled || savingSection}
                                    onClick={isBannerSaveDisabled || savingSection ? undefined : handleSaveSection}
                                    style={{
                                        ...ghostButtonStyle(COLORS.primary),
                                        backgroundColor: COLORS.primary,
                                        color: "#FFFFFF",
                                        opacity: (isBannerSaveDisabled || savingSection) ? 0.5 : 1,
                                        cursor: (isBannerSaveDisabled || savingSection) ? "not-allowed" : "pointer",
                                    }}
                                >
                                    {savingSection ? "SAVING..." : "SAVE"}
                                </button>
                            </>
                        );
                    })() : (
                        <>
                            <button
                                type="button"
                                onClick={handleToggleStatus}
                                // Activate in green, Suspend stays orange.
                                style={ghostButtonStyle(isActive ? "#FF9500" : "#008A67")}
                            >
                                {isActive ? "SUSPEND" : "ACTIVATE"}
                            </button>
                            <button
                                type="button"
                                onClick={handleDelete}
                                style={ghostButtonStyle("#FF414D")}
                            >
                                DELETE
                            </button>
                            <button
                                type="button"
                                onClick={handleEditClick}
                                style={ghostButtonStyle(COLORS.primary)}
                            >
                                EDIT
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Tabs Bar */}
            <div style={{ display: "flex", alignItems: "center", gap: fluid(10), marginTop: fluid(20), flexWrap: "wrap", flexShrink: 0 }}>
                {TAB_DISPLAY_ORDER
                    .map((label) => TABS.find((tab) => tab.label === label))
                    .filter(({ label }) => (
                        isStaffRoute
                            ? (label !== "Courses & Batches (4)" && label !== "Fees")
                            : (label !== "Leaves")
                    )).map(({ label, width }) => {
                        const isTabActive = activeTab === label;
                        const displayLabel = label.startsWith("Courses & Batches")
                            ? `Courses & Batches (${enrolledCoursesCount || 0})`
                            : label;
                        return (
                            <button
                                key={label}
                                type="button"
                                onClick={() => handleTabClick(label)}
                                style={{
                                    width: fluid(width),
                                    height: fluid(45),
                                    padding: `${fluid(13)} ${fluid(32)}`,
                                    boxSizing: "border-box",
                                    borderRadius: fluid(12),
                                    border: `1px solid ${COLORS.primary}`,
                                    backgroundColor: isTabActive ? COLORS.primary : "#FFFFFF",
                                    color: isTabActive ? "#FFFFFF" : COLORS.primary,
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: fluid(16),
                                    fontWeight: 700,
                                    letterSpacing: 0,
                                    lineHeight: "normal",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: fluid(10),
                                    whiteSpace: "nowrap",
                                    textTransform: "uppercase",
                                }}
                            >
                                {displayLabel}
                            </button>
                        );
                    })}
            </div>

            {previewOpen && <ImagePreviewOverlay src={avatarSrc} onClose={() => setPreviewOpen(false)} />}
        </div>
    );
};

export default ProfileHeader;
